import { FormEvent, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { GlassCard } from "@/components/common/GlassCard";
import { AuthPageShell } from "@/components/layout/AuthPageShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { resetPassword, user } = useAuth();

  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to send reset email";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthPageShell showBeta>
      <GlassCard className="border-indigo-300/25 bg-slate-950/45 p-7 shadow-[0_25px_80px_-45px_rgba(79,70,229,0.75)]">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold text-white">Reset password</h1>
          <p className="mt-1 text-xs text-[var(--lm-muted)] dark:text-slate-300/85">
            Enter your email and we'll send you a link to reset your password
          </p>
        </div>

        {success ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-green-500/30 bg-green-500/15 px-4 py-3 text-sm text-green-300">
              Check your email for a password reset link. The link will expire in 1 hour.
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full bg-slate-800 text-white hover:bg-slate-700"
              onClick={() => navigate("/login")}
            >
              Back to login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-200" htmlFor="email">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="email"
              />
            </div>

            {errorMessage ? (
              <p className="rounded-lg border border-red-500/30 bg-red-500/15 px-3 py-2 text-sm text-red-300">
                {errorMessage}
              </p>
            ) : null}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send reset link"}
            </Button>
          </form>
        )}

        <p className="mt-4 text-center text-sm text-[var(--lm-muted)] dark:text-slate-300/85">
          Remember your password?{" "}
          <Link
            to="/login"
            className="font-medium text-violet-300 transition-colors hover:text-violet-200"
          >
            Sign in
          </Link>
        </p>
      </GlassCard>
    </AuthPageShell>
  );
}
